import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import HarmonicsReportDialog from "../components/HarmonicsReportDialog";

const PHASES = ["L1", "L2", "L3"];
const OVERVIEW_HOURS = 168; // 7 days
const REFRESH_MS = 30000; // 30s
const LOCALE_MAP = {
  tr: "tr-TR",
  en: "en-US",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  ru: "ru-RU",
};

export default function ReactiveHarmonicsPage({ deviceType = "reactive" }) {
  const { t, i18n } = useTranslation();
  const { deviceId } = useParams();
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedView, setSelectedView] = useState("voltage-L1");
  const [trendRange, setTrendRange] = useState("1D");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const saved = JSON.parse(localStorage.getItem("selectedDevice") || "{}");
  const isVirtual = saved.mode === "virtual";
  const targetId = isVirtual ? saved.virtualId : deviceId;
  const locale = LOCALE_MAP[i18n.language] || "en-US";
  const endpointPrefix = deviceType === "analyzer" ? "measurements" : "reactive";
  const viewTypes = useMemo(() => {
    const base = [
      { key: "voltage", label: `${t("harmonics.voltage")} (L-N)` },
      { key: "current", label: t("harmonics.current") },
    ];
    if (deviceType === "analyzer") {
      return [
        { key: "voltage", label: `${t("harmonics.voltage")} (L-N)` },
        { key: "voltageLL", label: `${t("harmonics.voltage")} (L-L)` },
        { key: "current", label: t("harmonics.current") },
      ];
    }
    return base;
  }, [deviceType, t]);

  useEffect(() => {
    let active = true;

    async function fetchOverview() {
      if (!targetId) return;
      try {
        const res = await fetch(
          `${API_BASE}/${endpointPrefix}/harmonics-overview?deviceId=${targetId}&hours=${OVERVIEW_HOURS}`
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        const json = await res.json();
        if (!active) return;
        setOverview(json);
      } catch (err) {
        console.error("fetch harmonics overview error:", err);
      } finally {
        if (active) setLoading(false);
      }
    }

    setLoading(true);
    fetchOverview();
    const interval = setInterval(fetchOverview, REFRESH_MS);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [API_BASE, targetId, endpointPrefix]);

  const [selectedType, selectedPhase] = selectedView.split("-");
  const selectedTypeLabel =
    viewTypes.find((v) => v.key === selectedType)?.label || t("harmonics.voltage");
  const viewLabel = `${selectedTypeLabel} - ${selectedPhase}`;

  useEffect(() => {
    const isValid = viewTypes.some((v) => `${v.key}-${selectedPhase}` === selectedView);
    if (!isValid) setSelectedView("voltage-L1");
  }, [selectedView, selectedPhase, viewTypes]);

  const spectrumData =
    overview?.spectrum?.[selectedType]?.[selectedPhase]?.map((item) => ({
      ...item,
      fill: item.order % 2 === 0 ? "#8b5cf6" : "#22c55e",
    })) || [];

  const trendData = useMemo(() => {
    const now = Date.now();
    const rangeMsMap = {
      "1H": 60 * 60 * 1000,
      "3H": 3 * 60 * 60 * 1000,
      "1D": 24 * 60 * 60 * 1000,
      "3D": 3 * 24 * 60 * 60 * 1000,
      "1W": 7 * 24 * 60 * 60 * 1000,
    };
    const minTs = now - (rangeMsMap[trendRange] || rangeMsMap["1D"]);

    return (overview?.trend || []).map((row) => ({
      ...row,
      ts: new Date(row.time).getTime(),
      timeLabel: new Date(row.time).toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    })).filter((row) => row.ts >= minTs);
  }, [overview, locale, trendRange]);

  const summaryCards = PHASES.map((phase) => {
    const v = overview?.summary?.[`THDV_${phase}`];
    const i = overview?.summary?.[`THDI_${phase}`];
    const worst = Math.max(v ?? 0, i ?? 0);
    const severity =
      worst >= 8 ? "text-red-600" : worst >= 5 ? "text-amber-500" : "text-emerald-600";

    return {
      phase,
      thdv: v,
      thdi: i,
      severity,
    };
  });

  if (loading) {
    return <div className="p-6 text-slate-600">{t("common.loading")}</div>;
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      <div className="rounded-2xl p-4 sm:p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">{t("harmonics.title")}</h1>
            <p className="text-sm text-cyan-100 mt-1">{t("harmonics.subtitle")}</p>
          </div>
          <div className="text-xs text-cyan-100 bg-white/10 px-3 py-2 rounded-lg">
            {t("harmonics.updatedAt")}:{" "}
            {overview?.fetchedAt
              ? new Date(overview.fetchedAt).toLocaleString(locale)
              : "-"}
          </div>
          <button
            onClick={() => setReportOpen(true)}
            className="bg-sky-600 text-white px-3 py-2 rounded-md hover:bg-sky-700 text-sm"
          >
            {t("harmonics.reportButton")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.phase}
            className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4"
          >
            <div className="text-xs uppercase tracking-wide text-slate-500">{card.phase}</div>
            <div className={`text-2xl font-extrabold mt-2 ${card.severity}`}>
              THDV: {card.thdv == null ? "-" : `${card.thdv.toFixed(2)}%`}
            </div>
            <div className={`text-lg font-semibold mt-1 ${card.severity}`}>
              THDI: {card.thdi == null ? "-" : `${card.thdi.toFixed(2)}%`}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-slate-800">
            {t("harmonics.spectrumTitle")} ({t("harmonics.instant")})
          </h2>
          <div className="flex flex-col items-start gap-1 w-full sm:w-auto">
            <label htmlFor="harmonic-view" className="text-xs font-semibold text-slate-500">
              {t("harmonics.viewSelector")}
            </label>
            <select
              id="harmonic-view"
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 w-full sm:w-auto sm:min-w-[210px]"
            >
              {viewTypes.flatMap((type) =>
                PHASES.map((phase) => (
                  <option key={`${type.key}-${phase}`} value={`${type.key}-${phase}`}>
                    {type.label} - {phase}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-2">
          {t("harmonics.showingSpectrum", { view: viewLabel })}
        </p>

        <div className="h-[260px] sm:h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={spectrumData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="harmFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="harmonic" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} unit="%" />
              <Tooltip formatter={(v) => `${Number(v).toFixed(2)}%`} />
              <ReferenceLine y={5} stroke="#ef4444" strokeDasharray="5 5" />
              <Bar
                dataKey="value"
                name={t("harmonics.amplitude")}
                fill="url(#harmFill)"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-slate-800">{t("harmonics.trendTitle")}</h2>
          <div className="flex gap-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
            {[
              { key: "1H", label: t("timeRanges.1H") },
              { key: "3H", label: t("timeRanges.3H") },
              { key: "1D", label: t("timeRanges.1D") },
              { key: "3D", label: t("timeRanges.3D") },
              { key: "1W", label: t("timeRanges.1W") },
            ].map((r) => (
              <button
                key={r.key}
                onClick={() => setTrendRange(r.key)}
                className={`px-3 py-1.5 rounded-md border text-sm font-medium whitespace-nowrap transition ${
                  trendRange === r.key
                    ? "bg-cyan-700 text-white border-cyan-700"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {deviceType === "analyzer" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3 sm:gap-4">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700 mb-2">THDI (Current)</div>
              <div className="h-[220px] sm:h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="timeLabel" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(v) => (v == null ? "-" : `${Number(v).toFixed(2)}%`)} />
                    <Legend />
                    <Line type="monotone" dataKey="THDI_L1" name="THDI L1" stroke="#f97316" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="THDI_L2" name="THDI L2" stroke="#e11d48" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="THDI_L3" name="THDI L3" stroke="#0f766e" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700 mb-2">THDV (L-N)</div>
              <div className="h-[220px] sm:h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="timeLabel" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(v) => (v == null ? "-" : `${Number(v).toFixed(2)}%`)} />
                    <Legend />
                    <Line type="monotone" dataKey="THDV_L1" name="THDV L1" stroke="#2563eb" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="THDV_L2" name="THDV L2" stroke="#16a34a" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="THDV_L3" name="THDV L3" stroke="#7c3aed" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700 mb-2">THDV (L-L)</div>
              <div className="h-[220px] sm:h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="timeLabel" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(v) => (v == null ? "-" : `${Number(v).toFixed(2)}%`)} />
                    <Legend />
                    <Line type="monotone" dataKey="THDV_LL12" name="THDV L1-2" stroke="#0891b2" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="THDV_LL23" name="THDV L2-3" stroke="#4f46e5" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="THDV_LL31" name="THDV L3-1" stroke="#0f766e" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-[260px] sm:h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="timeLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} unit="%" />
                <Tooltip formatter={(v) => (v == null ? "-" : `${Number(v).toFixed(2)}%`)} />
                <Legend />
                <Line type="monotone" dataKey="THDV_L1" name="THDV L1" stroke="#2563eb" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="THDV_L2" name="THDV L2" stroke="#16a34a" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="THDV_L3" name="THDV L3" stroke="#7c3aed" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="THDI_L1" name="THDI L1" stroke="#f97316" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="THDI_L2" name="THDI L2" stroke="#e11d48" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="THDI_L3" name="THDI L3" stroke="#0f766e" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <HarmonicsReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        deviceId={targetId}
        deviceType={deviceType}
        API_BASE={API_BASE}
        isVirtual={isVirtual}
      />
    </div>
  );
}


